/**
 * @file Task detail assignment control workflows.
 */

import {
    updateControlValue,
} from '../../shared/ui/controls/controlCore.js';
import {
    openConfirmationModal,
} from '../../shared/ui/confirmationModal.js';
import {
    sanitizeHTML,
} from '../../shared/utils/contentUtils.js';
import {
    getUserFullDisplayName,
} from '../../shared/utils/userDisplay.js';
import {
    getTaskAssignedUserPickerContext,
} from './taskDetails.js';

function isTeamAssignmentValue(value) {
    return Array.isArray(value) &&
        value.some(entry => String(entry).startsWith('team:'));
}

/**
 * Handles task assigned-user changes that select a team expansion option.
 * @param {object} params Handler parameters.
 * @param {CustomEvent} params.event Registered control change event.
 * @param {object} params.item Task item.
 * @param {string} params.itemType Detail item type.
 * @param {*} params.value New assigned-users value.
 * @param {Function} params.saveRegisteredDetailValue Live-save queue callback.
 * @returns {boolean} Whether the value change was handled.
 */
export function handleTaskAssignedUsersTeamSelection({
    event,
    item,
    itemType,
    value,
    saveRegisteredDetailValue,
}) {
    if (itemType !== 'task' || event.detail?.name !== 'assigned_users' ||
        !isTeamAssignmentValue(value)) {
        return false;
    }

    const teamSelection = value.find(entry =>
        String(entry).startsWith('team:'),
    );
    const userValues = value.filter(entry =>
        !String(entry).startsWith('team:'),
    );
    if (event.detail?.id) {
        updateControlValue(event.detail.id, userValues, { silent: true });
    }

    const teamId = String(teamSelection).replace('team:', '');
    const assignmentContext = getTaskAssignedUserPickerContext(item);
    const team = assignmentContext.visibleTeams.find(entry =>
        entry.id === teamId,
    );
    const memberIds = team?.members || [];
    const eligibleMemberIds = team?.eligibleMemberIds || [];
    const memberListHtml = memberIds.length > 0
        ? `<ul class="mt-3 space-y-1 text-left">${memberIds.map(uid => {
            const user = assignmentContext.allUsersMap.get(uid) ||
                assignmentContext.allowedUserMap.get(uid);
            const canAdd = assignmentContext.allowedUserMap.has(uid);
            const isAlreadyAssigned = userValues.includes(uid);
            const statusText = !canAdd
                ? '<span class="text-xs text-red-600">Will not add</span>'
                : isAlreadyAssigned
                    ? '<span class="text-xs text-gray-400">Already assigned</span>'
                    : '';
            return `<li class="flex items-center justify-between gap-3 rounded bg-gray-50 px-3 py-2 text-sm">
        <span>${sanitizeHTML(getUserFullDisplayName(user || { id: uid, name: uid }))}</span>
        ${statusText}
      </li>`;
        }).join('')}</ul>`
        : '<p class="mt-3 text-sm text-gray-500">This team has no eligible members for this task.</p>';
    const hasIneligibleUsers = memberIds.some(uid =>
        !assignmentContext.allowedUserMap.has(uid),
    );
    const permissionNote = hasIneligibleUsers
        ? '<p class="mt-3 text-sm text-gray-700">Users marked as will not add can&apos;t be added to this subtask due to insufficient permissions.</p>'
        : '';

    openConfirmationModal(
        `Add ${team?.name || 'Team'} Members`,
        `<p class="text-sm text-gray-600">This will add the team&apos;s current members to <strong>Assigned Users</strong>.</p>${memberListHtml}${permissionNote}`,
        async () => {
            const nextAssignedUsers = Array.from(new Set([
                ...userValues,
                ...eligibleMemberIds,
            ]));
            if (event.detail?.id) {
                updateControlValue(
                    event.detail.id,
                    nextAssignedUsers,
                    { silent: true },
                );
            }
            await saveRegisteredDetailValue({
                item,
                itemType,
                fieldName: 'assigned_users',
                value: nextAssignedUsers,
            });
        },
        false,
        'Add Members',
    );

    return true;
}
